import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Badge, Button, Form, Spinner } from 'react-bootstrap';
import { useAppSelector } from '../../store/hooks';
import {
  chatAPI,
  ChatLang,
  ChatMessage,
  ChatThread,
  loadChatLang,
  messageText,
  previewText,
  saveChatLang,
  vedSourcerChatAPI,
} from '../../services/chatApi';
import { getChatSocket } from '../../services/chatSocket';
import './ChatMessenger.css';

const THREAD_LIMIT = 50;
const DEFAULT_LEFT = 240;
const DEFAULT_RIGHT = 280;
const MIN_SIDE = 160;
const MAX_SIDE = 480;
const ARTICLE_LINK = '/technical-specs';

function colsStorageKey(userId?: string | null) {
  return `product_chat_cols_${userId || 'anon'}`;
}

function loadColWidths(userId?: string | null) {
  try {
    const raw = JSON.parse(localStorage.getItem(colsStorageKey(userId)) || '{}');
    return {
      left: Math.min(MAX_SIDE, Math.max(MIN_SIDE, Number(raw.left) || DEFAULT_LEFT)),
      right: Math.min(MAX_SIDE, Math.max(MIN_SIDE, Number(raw.right) || DEFAULT_RIGHT)),
    };
  } catch {
    return { left: DEFAULT_LEFT, right: DEFAULT_RIGHT };
  }
}

function saveColWidths(userId: string | null | undefined, left: number, right: number) {
  localStorage.setItem(colsStorageKey(userId), JSON.stringify({ left, right }));
}

function articleHref(article: string) {
  return `${ARTICLE_LINK}?search=${encodeURIComponent(article)}`;
}

function mentionQuery(text: string) {
  const m = String(text || '').match(/@([^\s@]*)$/);
  return m ? m[1] : null;
}

function fileFromClipboard(data: DataTransfer | null): File | null {
  if (!data) return null;
  const items = Array.from(data.items || []);
  for (const item of items) {
    if (item.kind === 'file') {
      const f = item.getAsFile();
      if (f) return f;
    }
  }
  const files = data.files;
  return files?.[0] || null;
}

async function downloadUrl(url: string, name?: string | null) {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = name || 'attachment';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

interface Props {
  initialProductId?: string;
  compact?: boolean;
}

const ChatMessenger: React.FC<Props> = ({ initialProductId, compact }) => {
  const user = useAppSelector((s) => s.auth.user);
  const [lang, setLang] = useState<ChatLang>(() => loadChatLang());
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | undefined>(initialProductId);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [text, setText] = useState('');
  const [mentionCandidates, setMentionCandidates] = useState<Array<{ id: string; label: string; username?: string | null; role?: string | null }>>([]);
  const [sending, setSending] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [pendingPreview, setPendingPreview] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<{ url: string; name?: string | null } | null>(null);
  const [canEditTz, setCanEditTz] = useState(false);
  const [tzRu, setTzRu] = useState('');
  const [tzEn, setTzEn] = useState('');
  const [tzNameRu, setTzNameRu] = useState('');
  const [tzNameEn, setTzNameEn] = useState('');
  const [article, setArticle] = useState('');
  const [internalArticle, setInternalArticle] = useState('');
  const [headerName, setHeaderName] = useState('');
  const [analystName, setAnalystName] = useState('');
  const [sourcerName, setSourcerName] = useState('');
  const [translating, setTranslating] = useState(false);
  const [savingTz, setSavingTz] = useState(false);
  const [leftW, setLeftW] = useState(() => loadColWidths().left);
  const [rightW, setRightW] = useState(() => loadColWidths().right);
  const bottomRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const nextCursorRef = useRef<string | null>(null);
  const dragRef = useRef<{ side: 'left' | 'right'; startX: number; startW: number } | null>(null);
  const widthsRef = useRef({ left: leftW, right: rightW });
  const selectedIdRef = useRef<string | undefined>(selectedId);

  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  useEffect(() => {
    widthsRef.current = { left: leftW, right: rightW };
  }, [leftW, rightW]);

  useEffect(() => {
    const saved = loadColWidths(user?.id);
    setLeftW(saved.left);
    setRightW(saved.right);
  }, [user?.id]);

  useEffect(() => {
    if (!file || !file.type.startsWith('image/')) {
      setPendingPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPendingPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    const qn = mentionQuery(text);
    if (qn == null) {
      setMentionCandidates([]);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const data = await vedSourcerChatAPI.mentionCandidates(qn);
        if (!cancelled) setMentionCandidates(Array.isArray(data?.users) ? data.users : []);
      } catch {
        if (!cancelled) setMentionCandidates([]);
      }
    }, qn ? 150 : 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [text]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightbox(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);

  const clearFile = () => {
    setFile(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const pickFile = (next: File | null) => {
    if (!next) {
      clearFile();
      return;
    }
    setFile(next);
  };

  const onPasteAttach = (e: React.ClipboardEvent) => {
    if (!selectedId || sending) return;
    const pasted = fileFromClipboard(e.clipboardData);
    if (!pasted) return;
    e.preventDefault();
    pickFile(pasted);
  };

  const loadThreadsPage = useCallback(
    async (opts: { reset: boolean; cursor?: string | null; query?: string }) => {
      const query = opts.query !== undefined ? opts.query : q;
      if (opts.reset) setLoadingThreads(true);
      else setLoadingMore(true);
      try {
        const res: any = await chatAPI.getThreads({
          q: query || undefined,
          limit: THREAD_LIMIT,
          cursor: opts.reset ? undefined : opts.cursor || undefined,
        });
        const page: ChatThread[] = res.threads || [];
        const cursor = res.next_cursor || null;
        nextCursorRef.current = cursor;
        setNextCursor(cursor);
        setThreads((prev) => {
          if (!opts.reset) return [...prev, ...page];
          const sid = selectedIdRef.current;
          if (!sid) return page;
          const inPage = page.some((t) => String(t.product_id) === String(sid));
          if (inPage) return page;
          const kept = prev.find((t) => String(t.product_id) === String(sid));
          return kept ? [kept, ...page] : page;
        });
      } catch {
        if (opts.reset) setThreads([]);
      } finally {
        setLoadingThreads(false);
        setLoadingMore(false);
      }
    },
    [q]
  );

  const refreshThreads = useCallback(async () => {
    nextCursorRef.current = null;
    setNextCursor(null);
    await loadThreadsPage({ reset: true, query: q });
  }, [loadThreadsPage, q]);

  useEffect(() => {
    const t = window.setTimeout(() => setQ(qInput.trim()), 300);
    return () => window.clearTimeout(t);
  }, [qInput]);

  useEffect(() => {
    nextCursorRef.current = null;
    setNextCursor(null);
    void loadThreadsPage({ reset: true, query: q });
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const onThreadsScroll = () => {
    const el = listRef.current;
    if (!el || loadingThreads || loadingMore || !nextCursorRef.current) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
      void loadThreadsPage({ reset: false, cursor: nextCursorRef.current });
    }
  };

  const loadMoreThreads = () => {
    if (loadingThreads || loadingMore || !nextCursorRef.current) return;
    void loadThreadsPage({ reset: false, cursor: nextCursorRef.current });
  };

  const onResizeStart = (side: 'left' | 'right', e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = {
      side,
      startX: e.clientX,
      startW: side === 'left' ? leftW : rightW,
    };
    const onMove = (ev: MouseEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const delta = ev.clientX - drag.startX;
      if (drag.side === 'left') {
        setLeftW(Math.min(MAX_SIDE, Math.max(MIN_SIDE, drag.startW + delta)));
      } else {
        setRightW(Math.min(MAX_SIDE, Math.max(MIN_SIDE, drag.startW - delta)));
      }
    };
    const onUp = () => {
      dragRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      saveColWidths(user?.id, widthsRef.current.left, widthsRef.current.right);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const loadThread = useCallback(async (productId: string) => {
    setLoadingMsgs(true);
    try {
      const [detail, msgs]: any[] = await Promise.all([
        chatAPI.getThread(productId),
        chatAPI.getMessages(productId),
      ]);
      const product = detail.product;
      setCanEditTz(Boolean(detail.can_edit_tz));
      setArticle(product?.article || '');
      setInternalArticle(product?.internal_article || '');
      setHeaderName(product?.name || product?.eng_name || '');
      setAnalystName(product?.analyst_name || '');
      setSourcerName(product?.sourcer_name || '');
      setTzRu(product?.desc || '');
      setTzEn(product?.eng_desc || '');
      setTzNameRu(product?.name || '');
      setTzNameEn(product?.eng_name || '');
      setMessages(msgs.messages || []);
      setThreads((prev) => {
        const id = String(productId);
        const stub: ChatThread = {
          product_id: Number(product?.id ?? productId),
          article: product?.article || null,
          title: product?.name || product?.eng_name || null,
          name: product?.name || null,
          eng_name: product?.eng_name || null,
          thumb_url: product?.thumb_url || product?.image_url_1 || null,
          has_unread: false,
          unread_count: 0,
        };
        const idx = prev.findIndex((t) => String(t.product_id) === id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...next[idx], ...stub, has_unread: false, unread_count: 0 };
          return next;
        }
        return [stub, ...prev];
      });
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch (e: any) {
      setMessages([]);
      setArticle('');
      setInternalArticle('');
      setHeaderName('');
      setAnalystName('');
      setSourcerName('');
      setTzRu('');
      setTzEn('');
      setTzNameRu('');
      setTzNameEn('');
      alert(e?.response?.data?.error || e.message || 'Failed to open chat');
    } finally {
      setLoadingMsgs(false);
    }
  }, []);

  useEffect(() => {
    if (initialProductId != null && String(initialProductId) !== '') {
      setSelectedId(String(initialProductId));
    }
  }, [initialProductId]);

  useEffect(() => {
    if (selectedId) void loadThread(selectedId);
  }, [selectedId, loadThread]);

  useEffect(() => {
    const socket = getChatSocket();
    const clearUnread = (productId: string) => {
      setThreads((prev) =>
        prev.map((t) =>
          String(t.product_id) === String(productId)
            ? { ...t, has_unread: false, unread_count: 0 }
            : t
        )
      );
    };
    const onMessage = (msg: ChatMessage) => {
      const open = String(msg.product_id) === String(selectedId);
      if (open) {
        setMessages((prev) => {
          const i = prev.findIndex((m) => m.id === msg.id);
          if (i >= 0) {
            const next = [...prev];
            next[i] = msg;
            return next;
          }
          return [...prev, msg];
        });
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 30);
        void chatAPI.markRead(msg.product_id).then(() => clearUnread(String(msg.product_id)));
      } else {
        void loadThreadsPage({ reset: true, query: q });
      }
    };
    const onTz = (tz: any) => {
      if (String(tz.id || tz.productId) === String(selectedId) || String(tz.id) === String(selectedId)) {
        if (tz.desc != null) setTzRu(tz.desc);
        if (tz.eng_desc != null) setTzEn(tz.eng_desc);
        if (tz.name != null) setTzNameRu(tz.name);
        if (tz.eng_name != null) setTzNameEn(tz.eng_name);
      }
    };
    const onUnread = (payload?: { productId?: string | number }) => {
      if (selectedId && payload?.productId != null && String(payload.productId) === String(selectedId)) {
        void chatAPI.markRead(selectedId).then(() => clearUnread(String(selectedId)));
        return;
      }
      void loadThreadsPage({ reset: true, query: q });
    };

    if (selectedId) socket.emit('chat:join', selectedId);
    const onConnect = () => {
      if (selectedId) socket.emit('chat:join', selectedId);
    };
    socket.on('connect', onConnect);
    socket.on('chat:message', onMessage);
    socket.on('chat:tz', onTz);
    socket.on('chat:unread', onUnread);
    return () => {
      if (selectedId) socket.emit('chat:leave', selectedId);
      socket.off('connect', onConnect);
      socket.off('chat:message', onMessage);
      socket.off('chat:tz', onTz);
      socket.off('chat:unread', onUnread);
    };
  }, [selectedId, loadThreadsPage, q]);

  const onLangChange = (next: ChatLang) => {
    setLang(next);
    saveChatLang(next);
  };

  const send = async () => {
    if (!selectedId || (!text.trim() && !file)) return;
    setSending(true);
    try {
      const fd = new FormData();
      if (text.trim()) fd.append('text', text.trim());
      fd.append('source_lang', lang);
      if (file) fd.append('file', file);
      const res: any = await chatAPI.sendMessage(selectedId, fd);
      const msg = res.message;
      if (msg) {
        setMessages((prev) => {
          const i = prev.findIndex((m) => m.id === msg.id);
          if (i >= 0) {
            const next = [...prev];
            next[i] = msg;
            return next;
          }
          return [...prev, msg];
        });
      }
      setText('');
      clearFile();
      refreshThreads();
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 30);
    } catch (e: any) {
      alert(e?.response?.data?.error || e.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const translateTz = async () => {
    if (!tzRu.trim()) return;
    setTranslating(true);
    try {
      const res: any = await chatAPI.translate(tzRu, 'ru', 'en');
      setTzEn(res.text || '');
      if (tzNameRu.trim()) {
        const n: any = await chatAPI.translate(tzNameRu, 'ru', 'en');
        setTzNameEn(n.text || '');
      }
    } catch (e: any) {
      alert(e?.response?.data?.error || e.message || 'Translate failed');
    } finally {
      setTranslating(false);
    }
  };

  const saveTz = async () => {
    if (!selectedId || !canEditTz) return;
    setSavingTz(true);
    try {
      await chatAPI.updateTz(selectedId, {
        name: tzNameRu,
        desc: tzRu,
        eng_name: tzNameEn,
        eng_desc: tzEn,
      });
    } catch (e: any) {
      alert(e?.response?.data?.error || e.message || 'Save failed');
    } finally {
      setSavingTz(false);
    }
  };

  return (
    <>
    <div
      className={`chat-messenger ${compact ? 'chat-messenger-compact' : ''}`}
      onWheel={(e) => e.stopPropagation()}
      style={{
        gridTemplateColumns: `${leftW}px 6px 1fr 6px ${rightW}px`,
        flex: 1,
        minHeight: 0,
        height: '100%',
        width: '100%',
      }}
    >
      <aside className="chat-threads">
        <div className="chat-threads-head">
          <Form.Control
            size="sm"
            placeholder="Search article / name / id…"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setQ(qInput.trim());
                void refreshThreads();
              }
            }}
          />
          <Button size="sm" variant="outline-secondary" onClick={() => void refreshThreads()}>
            ↻
          </Button>
        </div>
        <div
          className="chat-threads-list"
          ref={listRef}
          onScroll={onThreadsScroll}
          onWheel={(e) => e.stopPropagation()}
        >
          {loadingThreads && (
            <div className="p-2 text-center">
              <Spinner size="sm" />
            </div>
          )}
          {threads.map((t) => (
            <button
              type="button"
              key={t.product_id}
              className={`chat-thread-item ${String(t.product_id) === String(selectedId) ? 'active' : ''}`}
              onClick={() => setSelectedId(String(t.product_id))}
            >
              {t.thumb_url ? (
                <img src={t.thumb_url} alt="" className="chat-thread-thumb" />
              ) : (
                <span className="chat-thread-thumb chat-thread-thumb-placeholder" />
              )}
              <div className="chat-thread-meta">
                <div className="d-flex justify-content-between gap-1">
                  <strong className="text-truncate">{t.article || t.product_id}</strong>
                  {t.has_unread ? <Badge bg="danger">{t.unread_count || '•'}</Badge> : null}
                </div>
                <div className="small text-muted text-truncate">{previewText(t, lang)}</div>
              </div>
            </button>
          ))}
          {loadingMore && (
            <div className="p-2 text-center">
              <Spinner size="sm" />
            </div>
          )}
          {!loadingThreads && threads.length === 0 && (
            <div className="p-3 small text-muted">
              {q ? 'Nothing found — try another query' : 'No chats yet'}
            </div>
          )}
          {!loadingThreads && !loadingMore && nextCursor && (
            <div className="p-2 text-center">
              <Button size="sm" variant="outline-secondary" onClick={loadMoreThreads}>
                Load more
              </Button>
            </div>
          )}
        </div>
      </aside>

      <div
        className="chat-col-resizer"
        role="separator"
        aria-orientation="vertical"
        onMouseDown={(e) => onResizeStart('left', e)}
      />

      <section className="chat-main" onPaste={onPasteAttach}>
        <div className="chat-main-head">
          <div className="chat-main-head-info min-w-0 flex-grow-1">
            {selectedId ? (
              <>
                <div className="fw-semibold text-truncate">
                  {article ? (
                    <Link
                      to={articleHref(article)}
                      className="chat-article-link"
                      title="Открыть ТЗ с поиском по артикулу"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {article}
                    </Link>
                  ) : (
                    `#${selectedId}`
                  )}
                  {internalArticle ? (
                    <span className="chat-main-head-muted"> · {internalArticle}</span>
                  ) : null}
                </div>
                <div className="small chat-main-head-muted text-truncate">
                  {[
                    headerName || null,
                    analystName ? `Аналитик: ${analystName}` : null,
                    sourcerName ? `Сорсер: ${sourcerName}` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              </>
            ) : (
              <div className="fw-semibold">Select a product</div>
            )}
          </div>
          <Form.Select
            size="sm"
            style={{ width: 90, flexShrink: 0 }}
            value={lang}
            onChange={(e) => onLangChange(e.target.value as ChatLang)}
          >
            <option value="ru">RU</option>
            <option value="en">EN</option>
            <option value="zh">ZH</option>
          </Form.Select>
        </div>
        <div className="chat-messages" onWheel={(e) => e.stopPropagation()}>
          {loadingMsgs && (
            <div className="p-3 text-center">
              <Spinner size="sm" />
            </div>
          )}
          {messages.map((m) => {
            const mine = String(m.sender_id) === String(user?.id);
            return (
              <div key={m.id} className={`chat-bubble-wrap ${mine ? 'mine' : 'theirs'}`}>
                <div className="chat-bubble">
                  <div className="chat-bubble-meta">
                    {m.sender_username || 'user'} · {new Date(m.created_at).toLocaleString()}
                  </div>
                  {messageText(m, lang) ? <div>{messageText(m, lang)}</div> : null}
                  {m.attachment_url ? (
                    <div className="chat-attach mt-1">
                      {m.attachment_mime?.startsWith('image/') ? (
                        <button
                          type="button"
                          className="chat-attach-img-btn"
                          onClick={() => setLightbox({ url: m.attachment_url!, name: m.attachment_name })}
                        >
                          <img src={m.attachment_url} alt={m.attachment_name || ''} className="chat-attach-img" />
                        </button>
                      ) : (
                        <span className="chat-attach-file">📎 {m.attachment_name || 'file'}</span>
                      )}
                      <button
                        type="button"
                        className="chat-attach-dl"
                        title="Скачать"
                        onClick={() => void downloadUrl(m.attachment_url!, m.attachment_name)}
                      >
                        ↓
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>
        <div className="chat-composer-wrap" onPaste={onPasteAttach}>
          {file ? (
            <div className="chat-pending-attach">
              {pendingPreview ? (
                <img src={pendingPreview} alt="" className="chat-pending-thumb" />
              ) : (
                <span className="chat-pending-icon">📎</span>
              )}
              <span className="chat-pending-name text-truncate">{file.name}</span>
              <button type="button" className="chat-pending-clear" title="Убрать вложение" onClick={clearFile}>
                ×
              </button>
            </div>
          ) : null}
          {mentionCandidates.length > 0 ? (
            <div className="chat-mention-list">
              {mentionCandidates.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setText((prev) => prev.replace(/@([^\s@]*)$/, `@${u.label} `));
                    setMentionCandidates([]);
                  }}
                >
                  {u.label}
                  {u.username && u.username !== u.label ? <span className="text-muted"> · {u.username}</span> : null}
                  {u.role ? <span className="text-muted"> · {u.role}</span> : null}
                </button>
              ))}
            </div>
          ) : null}
          <div className="chat-composer">
            <input
              ref={fileRef}
              type="file"
              className="d-none"
              onChange={(e) => pickFile(e.target.files?.[0] || null)}
            />
            <Button size="sm" variant="outline-secondary" disabled={!selectedId} onClick={() => fileRef.current?.click()}>
              📎
            </Button>
            <Form.Control
              size="sm"
              placeholder="Message, @name"
              value={text}
              disabled={!selectedId || sending}
              onChange={(e) => setText(e.target.value)}
              onPaste={onPasteAttach}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
            />
            <Button size="sm" variant="primary" disabled={!selectedId || sending || (!text.trim() && !file)} onClick={() => void send()}>
              {sending ? <Spinner size="sm" /> : 'Send'}
            </Button>
          </div>
        </div>
      </section>

      <div
        className="chat-col-resizer"
        role="separator"
        aria-orientation="vertical"
        onMouseDown={(e) => onResizeStart('right', e)}
      />

      <aside className="chat-tz">
        <div className="chat-tz-head">
          <span className="fw-semibold">TZ</span>
          {canEditTz ? (
            <div className="d-flex gap-1">
              <Button size="sm" variant="outline-primary" disabled={translating} onClick={() => void translateTz()}>
                {translating ? '…' : 'RU→EN'}
              </Button>
              <Button size="sm" variant="success" disabled={savingTz} onClick={() => void saveTz()}>
                {savingTz ? '…' : 'Save'}
              </Button>
            </div>
          ) : (
            <span className="small text-muted">EN only</span>
          )}
        </div>
        <div className="chat-tz-body">
          {canEditTz ? (
            <>
              <label className="small text-muted">Name (RU)</label>
              <Form.Control
                size="sm"
                className="mb-2"
                value={tzNameRu}
                onChange={(e) => setTzNameRu(e.target.value)}
              />
              <label className="small text-muted">TZ (RU)</label>
              <Form.Control
                as="textarea"
                rows={6}
                className="mb-2"
                value={tzRu}
                onChange={(e) => setTzRu(e.target.value)}
              />
              <label className="small text-muted">Name (EN)</label>
              <Form.Control
                size="sm"
                className="mb-2"
                value={tzNameEn}
                onChange={(e) => setTzNameEn(e.target.value)}
              />
              <label className="small text-muted">TZ (EN)</label>
              <Form.Control
                as="textarea"
                rows={6}
                value={tzEn}
                onChange={(e) => setTzEn(e.target.value)}
              />
            </>
          ) : (
            <>
              <div className="fw-semibold mb-2">{tzNameEn || '—'}</div>
              <pre className="chat-tz-readonly">{tzEn || '—'}</pre>
            </>
          )}
        </div>
      </aside>
    </div>
      {lightbox
        ? createPortal(
            <div className="chat-lightbox" role="dialog" aria-modal="true" onClick={() => setLightbox(null)}>
              <div className="chat-lightbox-inner" onClick={(e) => e.stopPropagation()}>
                <img src={lightbox.url} alt={lightbox.name || ''} className="chat-lightbox-img" />
                <div className="chat-lightbox-actions">
                  <Button size="sm" variant="light" onClick={() => void downloadUrl(lightbox.url, lightbox.name)}>
                    Скачать
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => setLightbox(null)}>
                    Закрыть
                  </Button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
};

export default ChatMessenger;
